<template>
  <div class="panel panel-default">

    <!-- ── Correct banner ─────────────────────────────────────────── -->
    <transition name="fade">
      <div v-if="answered" class="feedback feedback-correct text-center">
        <span class="feedback-text">✓ Correct! Well done!</span>
        <div class="progress-bar-wrap">
          <div class="progress-bar-fill" :style="{ width: progressWidth + '%' }"></div>
        </div>
      </div>
    </transition>

    <!-- ── Wrong banner ───────────────────────────────────────────── -->
    <transition name="fade">
      <div v-if="wrongAnswer" class="feedback feedback-wrong text-center">
        <span class="feedback-text">✗ Wrong! Try again.</span>
      </div>
    </transition>

    <!-- ── Question type label ────────────────────────────────────── -->
    <p class="q-type-label text-center">{{ qTypeLabel }}</p>

    <!-- ── SVG Shape ──────────────────────────────────────────────── -->
    <div class="text-center shape-container">
      <svg :viewBox="currentShape.viewBox" width="140" height="140">
        <path
          v-if="currentShape.path"
          :d="currentShape.path"
          fill="rgb(169, 247, 234)"
          stroke="rgb(3, 53, 48)"
          stroke-width="3"
        />
        <component
          :is="currentShape.tag"
          v-else
          v-bind="currentShape.attrs"
          fill="rgb(169, 247, 234)"
          stroke="rgb(3, 53, 48)"
          stroke-width="3"
        />
      </svg>
    </div>

    <!-- ── Question text ──────────────────────────────────────────── -->
    <h3 class="panel-title text-center q-text">{{ questionText }}</h3>

    <!-- ── Answer buttons ─────────────────────────────────────────── -->
    <div class="answer-grid">
      <button
        v-for="(btn, index) in btnData"
        :key="index"
        class="ans-btn"
        :disabled="answered"
        @click="onAnswer(btn.correct)"
      >
        {{ btn.label }}
      </button>
    </div>

    <!-- ── Next Question ──────────────────────────────────────────── -->
    <div class="text-center next-btn-wrap">
      <button class="next-btn" @click="nextQuestion">Next Question →</button>
    </div>

  </div>
</template>

<script>
import { SHAPES } from './shapesData.js';

// ─── Helpers ───────────────────────────────────────────────────────────────
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pick(arr, exclude) {
  const pool = arr.filter(x => x !== exclude);
  return pool[Math.floor(Math.random() * pool.length)];
}

// ─── Question generators ───────────────────────────────────────────────────

function makeIdentifyQuestion(shapes) {
  const pool    = shuffle(shapes);
  const correct = pool[0];
  const options = shuffle([
    { correct: true,  label: correct.name },
    { correct: false, label: pool[1].name },
    { correct: false, label: pool[2].name },
    { correct: false, label: pool[3].name },
  ]);
  return {
    shape: correct,
    qTypeLabel: "Shape Identification",
    questionText: "What shape is this?",
    buttons: options,
  };
}

function makePropsQuestion(shapes) {
  const pool    = shuffle(shapes);
  const correct = pool[0];

  // Pick a random property to ask about
  const propTypes = ["sides", "vertices", "angles"];
  const propType  = propTypes[randInt(0, propTypes.length - 1)];

  let questionText, correctLabel, wrongLabels;

  if (propType === "sides") {
    questionText  = `How many sides does a ${correct.name} have?`;
    correctLabel  = String(correct.sides);
    const sidesPool = [...new Set(shapes.map(s => s.sides))].filter(n => n !== correct.sides);
    wrongLabels   = shuffle(sidesPool).slice(0, 3).map(String);
  } else if (propType === "vertices") {
    questionText  = `How many vertices (corners) does a ${correct.name} have?`;
    correctLabel  = String(correct.vertices);
    const vPool   = [...new Set(shapes.map(s => s.vertices))].filter(n => n !== correct.vertices);
    wrongLabels   = shuffle(vPool).slice(0, 3).map(String);
  } else {
    questionText  = `Which best describes the angles of a ${correct.name}?`;
    correctLabel  = correct.angles;
    wrongLabels   = shuffle(shapes.filter(s => s.name !== correct.name))
                      .slice(0, 3)
                      .map(s => s.angles);
  }

  // Pad wrong labels if not enough distinct values
  while (wrongLabels.length < 3) {
    wrongLabels.push(String(randInt(1, 12)));
  }

  const options = shuffle([
    { correct: true,  label: correctLabel },
    { correct: false, label: wrongLabels[0] },
    { correct: false, label: wrongLabels[1] },
    { correct: false, label: wrongLabels[2] },
  ]);

  return {
    shape: correct,
    qTypeLabel: "Sides & Angles",
    questionText,
    buttons: options,
  };
}

function makeAreaQuestion(shapes) {
  // Only use shapes that have area formulas
  const areaShapes = shapes.filter(s => s.area !== null);
  const pool       = shuffle(areaShapes);
  const correct    = pool[0];

  const questionText = correct.areaQ;
  const correctLabel = correct.area;

  // Distractors: other shape formulas
  const wrongLabels = shuffle(areaShapes.filter(s => s.area !== correct.area))
    .slice(0, 3)
    .map(s => s.area);

  const options = shuffle([
    { correct: true,  label: correctLabel },
    { correct: false, label: wrongLabels[0] },
    { correct: false, label: wrongLabels[1] },
    { correct: false, label: wrongLabels[2] },
  ]);

  return {
    shape: correct,
    qTypeLabel: "Area Formula",
    questionText,
    buttons: options,
  };
}

// ─── Constants ────────────────────────────────────────────────────────────
const PROGRESS_DURATION = 2000;
const PROGRESS_INTERVAL = 30;

// ─── Component ────────────────────────────────────────────────────────────
export default {
  props: {
    category: { type: String, default: "all" },
  },

  data() {
    return {
      answered:      false,
      wrongAnswer:   false,
      progressWidth: 0,
      currentShape:  SHAPES[0],
      qTypeLabel:    "",
      questionText:  "",
      btnData:       [],
      _progressTimer: null,
      _autoTimer:     null,
    };
  },

  methods: {
    pickGenerator() {
      if (this.category === "identify") return makeIdentifyQuestion;
      if (this.category === "props")    return makePropsQuestion;
      if (this.category === "area")     return makeAreaQuestion;
      // "all" - weighted mix
      const r = Math.random();
      if (r < 0.35) return makeIdentifyQuestion;
      if (r < 0.70) return makePropsQuestion;
      return makeAreaQuestion;
    },

    generateQuestion() {
      this.answered      = false;
      this.wrongAnswer   = false;
      this.progressWidth = 0;
      clearTimeout(this._autoTimer);
      clearInterval(this._progressTimer);

      const gen = this.pickGenerator();
      const { shape, qTypeLabel, questionText, buttons } = gen(SHAPES);

      this.currentShape = shape;
      this.qTypeLabel   = qTypeLabel;
      this.questionText = questionText;
      this.btnData      = buttons;
    },

    onAnswer(isCorrect) {
      if (isCorrect) {
        this.wrongAnswer   = false;
        this.answered      = true;
        this.progressWidth = 0;
        const step = (PROGRESS_INTERVAL / PROGRESS_DURATION) * 100;
        this._progressTimer = setInterval(() => {
          this.progressWidth = Math.min(this.progressWidth + step, 100);
        }, PROGRESS_INTERVAL);
        this._autoTimer = setTimeout(() => {
          clearInterval(this._progressTimer);
          this.generateQuestion();
        }, PROGRESS_DURATION);
      } else {
        this.wrongAnswer = true;
      }
    },

    nextQuestion() {
      clearInterval(this._progressTimer);
      clearTimeout(this._autoTimer);
      this.generateQuestion();
    },
  },

  created() {
    this.generateQuestion();
  },

  beforeDestroy() {
    clearInterval(this._progressTimer);
    clearTimeout(this._autoTimer);
  },
};
</script>

<style scoped>
/* ── Feedback ─────────────────────────────────────────────────────── */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter, .fade-leave-to { opacity: 0; }

.feedback {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 17px;
  font-weight: bold;
}
.feedback-correct {
  background: rgba(3, 120, 60, 0.12);
  color: rgb(3, 100, 50);
  border: 2px solid rgba(3, 120, 60, 0.3);
  flex-direction: column;
  gap: 10px;
}
.feedback-wrong {
  background: rgba(180, 30, 30, 0.1);
  color: rgb(160, 20, 20);
  border: 2px solid rgba(180, 30, 30, 0.3);
}
.feedback-text { font-size: 1.1rem; }

/* ── Progress bar ─────────────────────────────────────────────────── */
.progress-bar-wrap {
  width: 100%;
  height: 6px;
  background: rgba(3, 120, 60, 0.2);
  border-radius: 4px;
  overflow: hidden;
}
.progress-bar-fill {
  height: 100%;
  background: rgb(3, 120, 60);
  border-radius: 4px;
  transition: width 30ms linear;
}

/* ── Question type label ─────────────────────────────────────────── */
.q-type-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: rgba(3, 53, 48, 0.5);
  margin-bottom: 0;
  margin-top: 6px;
}

/* ── Shape SVG ────────────────────────────────────────────────────── */
.shape-container {
  margin: 10px auto 4px;
}

/* ── Question text ────────────────────────────────────────────────── */
.q-text {
  font-size: 18px;
  font-weight: 600;
  color: rgb(3, 53, 48);
  padding: 10px 12px 6px;
  line-height: 1.5;
}

/* ── Answer grid ──────────────────────────────────────────────────── */
.answer-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 10px 16px 6px;
}
.ans-btn {
  min-height: 56px;
  padding: 8px 10px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 8px;
  border: 2px solid rgb(3, 53, 48);
  background: rgb(169, 247, 234);
  color: rgb(3, 53, 48);
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
  line-height: 1.3;
  word-break: break-word;
  hyphens: auto;
}
.ans-btn:hover:not(:disabled) {
  background: rgb(100, 225, 205);
  transform: scale(1.03);
}
.ans-btn:disabled { opacity: 0.55; cursor: default; }

/* ── Next Question ────────────────────────────────────────────────── */
.next-btn-wrap {
  margin-top: 16px;
  margin-bottom: 10px;
}
.next-btn {
  background-color: rgb(3, 53, 48);
  color: white;
  padding: 10px 28px;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  white-space: nowrap;
}
.next-btn:hover { background-color: rgb(5, 80, 70); }
</style>
